import { EditOutlined, PlusOutlined, StopOutlined, UndoOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PermissionCode, SystemRole, type RoleSummary } from "@autoflow/types";
import {
  Alert, Button, Card, Checkbox, Form, Input, Modal, Popconfirm, Result, Space, Table, Tag, Tooltip, Typography, message
} from "antd";
import { useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { rolesApi, type RoleInput } from "./roles-api";

type RoleFormValues = RoleInput;

function permissionLabel(code: string) {
  return code.split(".").map(part=>part.replaceAll("_"," ")).join(" · ");
}

export function RolesPage() {
  const { user } = useAuth();
  const { text } = useAppConfig();
  const queryClient = useQueryClient();
  const [modalOpen,setModalOpen] = useState(false);
  const [editing,setEditing] = useState<RoleSummary|null>(null);
  const [form] = Form.useForm<RoleFormValues>();
  const isAdmin = Boolean(user?.roles.includes(SystemRole.Admin) && user.permissions.includes(PermissionCode.RoleManage));
  const roles = useQuery({queryKey:["roles"],queryFn:rolesApi.list,enabled:isAdmin});
  const permissions = useQuery({queryKey:["role-permissions"],queryFn:rolesApi.permissions,enabled:isAdmin});
  const save = useMutation({
    mutationFn: (values:RoleFormValues) => editing
      ? rolesApi.update(editing.id,values)
      : rolesApi.create(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey:["roles"]});
      message.success(text.roleSaved);
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
    }
  });
  const setActive = useMutation({
    mutationFn: ({role,isActive}:{role:RoleSummary;isActive:boolean}) => rolesApi.update(role.id,{isActive}),
    onSuccess: async () => { await queryClient.invalidateQueries({queryKey:["roles"]}); }
  });

  if (!isAdmin) return <Result status="403" title="403" subTitle={text.accessDenied}/>;

  const openCreate = () => {
    save.reset();
    setEditing(null);
    form.setFieldsValue({name:"",description:"",permissionIds:[]});
    setModalOpen(true);
  };
  const openEdit = (role:RoleSummary) => {
    save.reset();
    setEditing(role);
    form.setFieldsValue({name:role.name,description:role.description??"",permissionIds:role.permissions.map(item=>item.id)});
    setModalOpen(true);
  };
  const protectedAdmin = Boolean(editing?.isSystem && editing.name===SystemRole.Admin);
  const columns = [
    {title:text.roleName,key:"name",render:(_:unknown,role:RoleSummary)=><Space><b>{role.name}</b>{role.isSystem&&<Tag>{text.systemRole}</Tag>}</Space>},
    {title:text.roleDescription,dataIndex:"description",key:"description",render:(value:string|null)=>value||"—"},
    {title:text.permissions,key:"permissions",render:(_:unknown,role:RoleSummary)=><Tooltip title={role.permissions.map(item=>item.code).join(", ")}><Tag color="blue">{role.permissions.length}</Tag></Tooltip>},
    {title:text.roleUsers,dataIndex:"userCount",key:"users"},
    {title:text.status,key:"status",render:(_:unknown,role:RoleSummary)=><Tag color={role.isActive?"green":"default"}>{role.isActive?text.enabled:text.disabled}</Tag>},
    {title:text.action,key:"actions",render:(_:unknown,role:RoleSummary)=>{
      const adminRole=role.isSystem&&role.name===SystemRole.Admin;
      return <Space>
        <Button type="link" icon={<EditOutlined/>} onClick={()=>openEdit(role)}>{text.edit}</Button>
        <Tooltip title={adminRole?text.adminRoleProtected:undefined}>
          <span><Popconfirm
            title={role.isActive?text.disableRole:text.enableRole}
            description={role.isActive?text.disableRoleConfirm:undefined}
            disabled={adminRole}
            onConfirm={()=>setActive.mutate({role,isActive:!role.isActive})}
          >
            <Button
              type="link"
              danger={role.isActive}
              disabled={adminRole||setActive.isPending}
              icon={role.isActive?<StopOutlined/>:<UndoOutlined/>}
            >{role.isActive?text.disableRole:text.enableRole}</Button>
          </Popconfirm></span>
        </Tooltip>
      </Space>;
    }}
  ];

  return <section>
    <div className="page-title"><div><Typography.Title level={2}>{text.roleManagement}</Typography.Title><Typography.Paragraph type="secondary">{text.roleManagementHint}</Typography.Paragraph></div><Button type="primary" icon={<PlusOutlined/>} onClick={openCreate}>{text.newRole}</Button></div>
    {(roles.isError||permissions.isError)&&<Alert type="error" showIcon message={(roles.error as Error)?.message||(permissions.error as Error)?.message} style={{marginBottom:16}}/>}
    <Card><Table<RoleSummary> rowKey="id" loading={roles.isLoading} dataSource={roles.data??[]} columns={columns} scroll={{x:900}}/></Card>
    <Modal
      open={modalOpen}
      width={760}
      title={editing?text.editRole:text.newRole}
      okText={text.save}
      confirmLoading={save.isPending}
      onCancel={()=>setModalOpen(false)}
      onOk={()=>void form.validateFields().then(values=>save.mutate(values))}
    >
      {protectedAdmin&&<Alert type="warning" showIcon message={text.adminRoleProtected} style={{marginBottom:16}}/>}
      {save.isError&&<Alert type="error" showIcon message={(save.error as Error).message} style={{marginBottom:16}}/>}
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="name" label={text.roleName} rules={[{required:true},{min:2},{max:64}]}>
          <Input disabled={Boolean(editing?.isSystem)}/>
        </Form.Item>
        <Form.Item name="description" label={text.roleDescription} rules={[{max:500}]}>
          <Input.TextArea rows={3}/>
        </Form.Item>
        <Form.Item name="permissionIds" label={text.permissions} rules={[{type:"array"}]}>
          <Checkbox.Group disabled={protectedAdmin} style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:10}}>
            {(permissions.data??[]).map(permission=><Checkbox key={permission.id} value={permission.id}>{permissionLabel(permission.code)}</Checkbox>)}
          </Checkbox.Group>
        </Form.Item>
      </Form>
    </Modal>
  </section>;
}
